/**
 * Bank holidays and absences expressed as the hours they take off a resource's
 * day, so slot validation judges a hand-picked date against the same
 * unavailability the heatmap and the utilisation criteria show.
 *
 * In live mode public holidays reach the client as absences, so both sources
 * are folded into one list per resource.
 */
import { type Absence, type BankHoliday, type WorkingHoursProfile } from '@/types';
import {
  calculateAbsenceMinutes,
  isBankHoliday,
  normalizeDurationType,
} from '@/utils/heatmap-utils';
import {
  WORKING_HOURS_PER_DAY,
  dayKeysInRange,
  profileHoursForDate,
  type UnavailableDay,
} from '@/lib/slotEditValidation';
import {
  DEFAULT_BUSINESS_HOURS_END,
  DEFAULT_BUSINESS_HOURS_START,
  dayWorkWindow,
  minutesToHHMM,
  hhmmToMinutes,
  type TimeWindow,
} from '@/lib/dayAvailability';

/** The bank holiday falling on a day, when there is one. */
function bankHolidayOn(date: Date, bankHolidays: BankHoliday[]): BankHoliday | undefined {
  return bankHolidays.find(holiday => isBankHoliday(date, [holiday]));
}

/** The reason to show for an absence a resource has on a day. */
function absenceReason(resourceId: string, date: Date, absences: Absence[]): string {
  const onDay = absences.find(absence => (
    String(absence.resourceId) === String(resourceId)
    && calculateAbsenceMinutes(resourceId, date, [absence], WORKING_HOURS_PER_DAY) > 0
  ));
  return onDay?.reason?.trim() || 'Absent';
}

/**
 * The part of a day's work window an absence removes, when it is known: a
 * morning, an afternoon, or an hourly absence's own span. Null for a full day
 * and for a half day whose half is not recorded — the hours are lost, but not
 * from any particular part of the day.
 *
 * This mirrors the backend's `AbsenceCalculator.BuildBlockedWindows`, so a
 * booking the client allows is one the API's availability agrees is free.
 */
function absenceWindow(
  absence: Absence,
  workWindow: { startMinutes: number; endMinutes: number },
): TimeWindow | null {
  const midpoint = workWindow.startMinutes
    + (workWindow.endMinutes - workWindow.startMinutes) / 2;
  const clamp = (minutes: number): number =>
    Math.max(workWindow.startMinutes, Math.min(workWindow.endMinutes, minutes));
  const window = (startMinutes: number, endMinutes: number): TimeWindow | null =>
    endMinutes > startMinutes
      ? { startTime: minutesToHHMM(startMinutes), endTime: minutesToHHMM(endMinutes) }
      : null;

  switch (normalizeDurationType(absence.type)) {
    case 'half-day-am':
      return window(workWindow.startMinutes, midpoint);
    case 'half-day-pm':
      return window(midpoint, workWindow.endMinutes);
    case 'hourly':
      if (!absence.startTime || !absence.endTime) return null;
      return window(clamp(hhmmToMinutes(absence.startTime)), clamp(hhmmToMinutes(absence.endTime)));
    default:
      return null;
  }
}

/**
 * The parts of the day the absences on it remove, or null when any of them
 * removes hours from a part of the day that is not recorded — a partial loss
 * of unknown placement cannot be reasoned about by time of day.
 */
function absenceWindowsOn(
  resourceId: string,
  date: Date,
  absences: Absence[],
  contracted: number,
  workWindow: { startMinutes: number; endMinutes: number } | null,
): TimeWindow[] | null {
  if (!workWindow) return null;

  const windows: TimeWindow[] = [];
  for (const absence of absences) {
    if (String(absence.resourceId) !== String(resourceId)) continue;
    if (calculateAbsenceMinutes(resourceId, date, [absence], contracted) <= 0) continue;
    const window = absenceWindow(absence, workWindow);
    if (!window) return null;
    windows.push(window);
  }
  return windows.length > 0 ? windows : null;
}

export interface ResourceUnavailableDaysInput {
  resourceId: string;
  /** Inclusive window to inspect. */
  startDate: Date;
  endDate: Date;
  bankHolidays?: BankHoliday[];
  absences?: Absence[];
  /** The resource's effective working profile, for the hours each day carries. */
  workingProfile?: WorkingHoursProfile;
  workingHoursPerDay?: number;
  /** The company's business hours, which bound the day an absence is placed in. */
  businessHoursStart?: number;
  businessHoursEnd?: number;
}

/**
 * The days in the window the resource cannot fully work, with the hours each
 * one loses. A day is left out when it loses nothing.
 */
export function resourceUnavailableDays(
  input: ResourceUnavailableDaysInput,
): UnavailableDay[] {
  const {
    resourceId, startDate, endDate, bankHolidays = [], absences = [],
    workingProfile, workingHoursPerDay = WORKING_HOURS_PER_DAY,
    businessHoursStart = DEFAULT_BUSINESS_HOURS_START,
    businessHoursEnd = DEFAULT_BUSINESS_HOURS_END,
  } = input;
  if (endDate < startDate) return [];

  const days: UnavailableDay[] = [];
  for (const key of dayKeysInRange(startDate, endDate)) {
    const date = new Date(`${key}T00:00:00`);
    const contracted = workingProfile
      ? profileHoursForDate(date, workingProfile)
      : workingHoursPerDay;
    if (contracted <= 0) continue;

    const holiday = bankHolidayOn(date, bankHolidays);
    if (holiday) {
      days.push({ date: key, reason: holiday.name || 'Bank holiday' });
      continue;
    }

    const lostHours = calculateAbsenceMinutes(resourceId, date, absences, contracted) / 60;
    if (lostHours <= 0) continue;
    if (lostHours >= contracted) {
      days.push({ date: key, reason: absenceReason(resourceId, date, absences) });
      continue;
    }
    const lostWindows = absenceWindowsOn(
      resourceId,
      date,
      absences,
      contracted,
      dayWorkWindow(date, workingProfile, businessHoursStart, businessHoursEnd),
    );
    days.push({
      date: key,
      lostHours: Math.round(lostHours * 10) / 10,
      reason: absenceReason(resourceId, date, absences),
      ...(lostWindows ? { lostWindows } : {}),
    });
  }
  return days;
}

/** `resourceUnavailableDays` for every resource a plan touches. */
export function unavailableDaysByResourceId(input: {
  resourceIds: string[];
  startDate: Date;
  endDate: Date;
  bankHolidays?: BankHoliday[];
  absences?: Absence[];
  workingProfileFor?: (resourceId: string) => WorkingHoursProfile | undefined;
  businessHoursStart?: number;
  businessHoursEnd?: number;
}): Record<string, UnavailableDay[]> {
  const {
    resourceIds, startDate, endDate, bankHolidays, absences, workingProfileFor,
    businessHoursStart, businessHoursEnd,
  } = input;
  const byResourceId: Record<string, UnavailableDay[]> = {};
  for (const resourceId of new Set(resourceIds.map(String))) {
    const days = resourceUnavailableDays({
      resourceId,
      startDate,
      endDate,
      bankHolidays,
      absences,
      workingProfile: workingProfileFor?.(resourceId),
      businessHoursStart,
      businessHoursEnd,
    });
    if (days.length > 0) byResourceId[resourceId] = days;
  }
  return byResourceId;
}
